import { useMemo } from 'react';
import type { AiAssistantDocumentContext } from '@asset-metrix/portal-ui';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../../store';
import { selectPageSidebarPreviewedDocument } from '../../store/states/ui';

import { documentsFromTableSelection } from './tableRowDocuments';

/**
 * Documents the assistant can offer as chat context: the document it previews
 * itself, the documents previewed in the page sidebars and the documents
 * selected in the table the user last selected rows in. Deduplicated by id,
 * because the same document can be previewed and selected at once.
 */
export function useDocumentContexts(
  previewedByAssistant: AiAssistantDocumentContext[]
): AiAssistantDocumentContext[] {
  const previewedInSidebars = useStore(
    useShallow((state) =>
      [
        selectPageSidebarPreviewedDocument(state, 'fullscreen'),
        selectPageSidebarPreviewedDocument(state, 'page'),
      ].filter((document) => document != null)
    )
  );
  const tableRowSelection = useStore((state) => state.tableRowSelection);

  const selectedInTable = useMemo(
    () => documentsFromTableSelection(tableRowSelection),
    [tableRowSelection]
  );

  return useMemo(() => {
    const byId = new Map<string, AiAssistantDocumentContext>();
    for (const document of [
      ...previewedByAssistant,
      ...previewedInSidebars,
      ...selectedInTable,
    ]) {
      if (!byId.has(document.id)) {
        byId.set(document.id, document);
      }
    }
    return [...byId.values()];
  }, [previewedByAssistant, previewedInSidebars, selectedInTable]);
}
